import React from "react";
import { EvaluateResponse, EvaluateResult } from "../../model_gen/chat";
import { Sentinel } from "../../model_gen/sentinel";
import { Button } from "../ui/button";
import EvaluationBlock from "./EvaluationBlock";
import { 
  PlusIcon,
  ShieldIcon,
  SparklesIcon
} from "lucide-react";

interface EvaluationBlockData {
  id: string;
  selectedSentinels: string[];
  editedSentinels: { [key: string]: Sentinel };
  versionId: string;
  hasRun: boolean;
  evaluationResponse?: EvaluateResponse;
  evaluationRunId?: number;
  evaluationRequestId?: string;
}

interface EvaluationBlocksListProps {
  evaluationBlocks: EvaluationBlockData[];
  selectedVersion: string;
  originalSentinels: Sentinel[];
  selectKey: number;
  runningEvaluations: { [key: string]: boolean };
  companyId?: number;
  canAddEvaluation: boolean;
  onSentinelChange: (value: (string | number)[], blockId: string) => void;
  onRunEvaluation: (blockId: string) => void;
  onAddEvaluation: () => void;
  onViewRequest: (evaluationRequestId: string, sentinelId: string) => React.ReactNode;
  isEvaluationRunning: () => boolean;
}

const EvaluationBlocksList: React.FC<EvaluationBlocksListProps> = ({
  evaluationBlocks,
  selectedVersion,
  originalSentinels,
  selectKey,
  runningEvaluations,
  companyId,
  canAddEvaluation,
  onSentinelChange,
  onRunEvaluation,
  onAddEvaluation,
  onViewRequest,
  isEvaluationRunning,
}) => {
  const filteredBlocks = evaluationBlocks.filter(
    (block) => block.versionId === selectedVersion
  );

  if (filteredBlocks.length === 0 && !canAddEvaluation) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <ShieldIcon className="w-5 h-5 text-maitai-green" />
            <h3 className="text-lg font-gc-med text-foreground">Evaluation</h3>
          </div>
          {filteredBlocks.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 bg-muted/50 rounded-full">
              <span className="text-xs font-gc-reg text-muted-foreground">
                {filteredBlocks.length} block{filteredBlocks.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>
        
        {canAddEvaluation && (
          <Button
            variant="outline"
            onClick={onAddEvaluation}
            className="gap-2 border-maitai-green text-maitai-green hover:bg-maitai-green hover:text-white"
          >
            <PlusIcon className="w-4 h-4" />
            Add Evaluation
          </Button>
        )}
      </div>

      {/* Evaluation Blocks */}
      {filteredBlocks.length > 0 ? (
        <div className="space-y-4">
          {filteredBlocks.map((block) => (
            <EvaluationBlock
              key={block.id}
              block={block}
              originalSentinels={originalSentinels}
              selectKey={selectKey}
              runningEvaluations={runningEvaluations}
              companyId={companyId}
              onSentinelChange={onSentinelChange}
              onRunEvaluation={onRunEvaluation}
              onViewRequest={onViewRequest}
              isEvaluationRunning={isEvaluationRunning}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-muted/20 rounded-full flex items-center justify-center mb-4">
            <SparklesIcon className="w-8 h-8 text-muted-foreground" />
          </div>
          <h4 className="text-lg font-gc-med text-foreground mb-2">
            No evaluations yet
          </h4>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
            Add an evaluation to test this request against your sentinels and see how well it performs.
          </p>
          {canAddEvaluation && (
            <Button
              onClick={onAddEvaluation}
              className="gap-2 bg-maitai-green hover:bg-maitai-green/90 text-white"
            >
              <PlusIcon className="w-4 h-4" />
              Add First Evaluation
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default EvaluationBlocksList; 